import { LocalDate } from 'js-joda';
import { StoreType } from '@tock/shared/utils/connect';
import {
  TicketGroupStatus,
  ticketGroupMatchesTicketTypeId,
  ticketGroupStatusForPartySize,
} from 'utils/calendar';
import { consumer, core } from '@tock/coretypes';
import { createSelector } from 'reselect';
import { getFeatures } from './featuresSelector';
import {
  isOfferingDeliveryType,
  isOfferingEventType,
  isOfferingPickupType,
  isOfferingReservationType,
  isOfferingShippingType,
} from 'utils/offerings';
import { oc } from 'ts-optchain';
import _ from 'lodash';

export interface SeparatedOfferings {
  events?: consumer.Offering[];
  reservations?: consumer.Offering[];
  pickup?: consumer.Offering[];
  delivery?: consumer.Offering[];
  shipping?: consumer.Offering[];
}

export const calendarSelector = (state: StoreType) => state.calendar;
export const calendarTicketTypesSelector = (state: StoreType) =>
  state.calendar.calendar?.ticketType ?? [];

export function experienceSelector(
  state: StoreType,
  experienceId: number
): core.ITicketType | undefined {
  const ticketTypeList = oc(state).calendar.calendar.ticketType([]);
  return _.find(ticketTypeList, (ticketType: core.ITicketType) => {
    return ticketType.id === experienceId;
  });
}

function getMatchingTicketGroups(
  ticketGroups: consumer.CalendarTicketGroup[],
  experienceId: number
) {
  return _.filter(ticketGroups, (tg) => ticketGroupMatchesTicketTypeId(tg, experienceId));
}

export function experienceTicketGroupSelector(
  state: StoreType,
  experienceId: number
): consumer.CalendarTicketGroup[] {
  const ticketGroupList = oc(state).calendar.calendar.ticketGroup(
    []
  ) as consumer.CalendarTicketGroup[];
  return getMatchingTicketGroups(ticketGroupList, experienceId);
}

export function experienceAvailableSizesSelector(state: StoreType, experienceId: number): number[] {
  const ticketType = experienceSelector(state, experienceId);
  if (_.isNil(ticketType)) {
    return [];
  }
  const ticketGroups = experienceTicketGroupSelector(state, experienceId);
  return _.filter(ticketType.partySize || [], (size: number) => {
    return _.some(
      ticketGroups,
      (tg) => ticketGroupStatusForPartySize(tg, size) === TicketGroupStatus.AVAILABLE
    );
  });
}

const getOfferings = (state: StoreType) => state.calendar.offerings;

export const computeSeparatedOfferings = (
  offerings?: consumer.ConsumerOfferings
): SeparatedOfferings => {
  const separatedOfferings: SeparatedOfferings = {};
  if (!offerings) {
    return separatedOfferings;
  }
  const offeringExperiences = filterByProductPermissions(offerings.experience);

  separatedOfferings.reservations = _.sortBy(
    _.filter(offeringExperiences as consumer.Offering[], (offering) => {
      return isOfferingReservationType(offering);
    }),
    'ordering'
  );
  separatedOfferings.events = _.sortBy(
    _.filter(offeringExperiences as consumer.Offering[], (offering) => {
      return isOfferingEventType(offering);
    }),
    'eventDetails.date',
    'eventDetails.startTime'
  );
  separatedOfferings.pickup = _.sortBy(
    _.filter(offeringExperiences as consumer.Offering[], (offering) => {
      return isOfferingPickupType(offering);
    }),
    'ordering'
  );
  separatedOfferings.delivery = [
    ..._.sortBy(
      _.filter(offeringExperiences as consumer.Offering[], (offering) => {
        return isOfferingDeliveryType(offering);
      }),
      'ordering'
    ),
  ];
  separatedOfferings.shipping = [
    ..._.sortBy(
      _.filter(offeringExperiences as consumer.Offering[], (offering) => {
        return isOfferingShippingType(offering);
      }),
      'ordering'
    ),
  ];

  return separatedOfferings;
};

export const offeringsSelector = createSelector(
  getOfferings,
  getFeatures,
  (offerings): consumer.ConsumerOfferings | undefined => {
    if (!offerings) {
      return undefined;
    }
    const experience = filterByProductPermissions(offerings.experience);

    return { ...offerings, experience: experience || [] };
  }
);

const filterAllExclusives = (offeringExperience: consumer.IOffering) => {
  return (
    !offeringExperience.isChaseSapphireExclusive &&
    offeringExperience.exclusivity !== 'RESERVE' &&
    offeringExperience.exclusivity !== 'SAPPHIRE'
  );
};

const filterByProductPermissions = (offeringExperiences: consumer.IOffering[]) => {
  return offeringExperiences.filter(filterAllExclusives);
};

export const separatedOfferingsSelector = createSelector(
  offeringsSelector,
  computeSeparatedOfferings
);

export const calendarOpenDateRangeSelector = createSelector(calendarSelector, ({ calendar }) => {
  if (!calendar?.openDate || calendar.openDate.length === 0) {
    return;
  }

  return {
    earliestDate: LocalDate.parse(calendar.openDate[0]),
    latestDate: LocalDate.parse(calendar.openDate[calendar.openDate.length - 1]),
  };
});
